import type { IFlowNodeContext } from '@hookpipe/contracts/flows/interfaces/node-context.interface';
import type { TFlowGraph } from '@hookpipe/contracts/flows/schemas/flow-graph.schema';
import {
  buildDominance,
  buildGraphIndex,
  type IDominance,
  type IGraphIndex,
} from '@hookpipe/contracts/flows/services/flow-graph.analysis';
import { explainReference } from '@hookpipe/contracts/flows/services/flow-graph.validator';
import type { IContextPath, TTemplateRoot } from '@hookpipe/contracts/flows/services/flow-template';

import type { components } from '../../shared/api/schema.gen';
import { pointTime } from '../../shared/lib/format';

export type TDraftSample = components['schemas']['DraftSampleDto'];

export type TValueType = 'text' | 'number' | 'boolean' | 'list' | 'null';

export const TYPE_TAG: Record<TValueType, string> = {
  text: 'abc',
  number: '123',
  boolean: 'y/n',
  list: '[ ]',
  null: 'null',
};

export type TKnownFields = components['schemas']['KnownFieldsResDto'];
export type TSampleTrigger = components['schemas']['SampleTriggerResDto'];

export interface IDataField {
  path: string;
  label: string;
  type: TValueType;
  value: unknown;
  preview?: string;
}

export interface IDataSource {
  key: string;
  name: string;
  type: string;
  /** From the last test run — the only data a verdict or a Code step's scope may trust. */
  hasSample: boolean;
  fields: IDataField[];
  origin?: string;
  missing?: string;
}

export const NOT_TESTED = 'Not in the last test run, so its fields are not known yet.';

export const missingText = (source: IDataSource): string =>
  source.missing ??
  (source.hasSample ? 'It held nothing to pick in the last test run.' : NOT_TESTED);

export interface IUnreadable {
  key: string;
  name: string;
  type: string;
  reason: string;
}

export interface IReaderData {
  sources: IDataSource[];
  unreadable: IUnreadable[];
  context: IFlowNodeContext | null;
  names: ReadonlyMap<string, string>;
  explain: (target: string) => string | null;
  /** `event` before any test, when the step reads the trigger's body straight. */
  event?: IDataSource;
  /** Where a template may start; a Library prompt reads `event`, `trigger`, `vars` only (§16). */
  roots?: readonly TTemplateRoot[];
}

// Mirrors the template path grammar: a key a path cannot spell is not offered at all.
const SEGMENT = /^[A-Za-z0-9_$-]+$/;
const FORBIDDEN = new Set(['__proto__', 'constructor', 'prototype']);
const MAX_FIELDS = 300;
const MAX_DEPTH = 8;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const valueType = (value: unknown): TValueType | null => {
  if (Array.isArray(value)) return 'list';
  if (value === null) return 'null';
  switch (typeof value) {
    case 'string':
      return 'text';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return null;
  }
};

export const fieldPreview = (field: IDataField): string => field.preview ?? sampleText(field.value);

export const sampleText = (value: unknown): string => {
  if (Array.isArray(value)) return value.length === 1 ? '1 item' : `${value.length} items`;
  if (typeof value === 'string') return value;
  return String(value);
};

const flatten = (value: unknown, prefix: string, label: string, into: IDataField[], depth = 0) => {
  if (into.length >= MAX_FIELDS) return;
  const type = valueType(value);
  if (type) {
    into.push({ path: prefix, label, type, value });
    return;
  }
  if (!isRecord(value) || depth >= MAX_DEPTH) return;
  for (const key of Object.keys(value)) {
    if (!SEGMENT.test(key) || FORBIDDEN.has(key)) continue;
    flatten(value[key], `${prefix}.${key}`, label ? `${label}.${key}` : key, into, depth + 1);
  }
};

export const fieldsOf = (value: unknown, prefix: string): IDataField[] => {
  const fields: IDataField[] = [];
  flatten(value, prefix, '', fields);
  return fields.map((field) => (field.label ? field : { ...field, label: prefix }));
};

// A webhook trigger's sample keeps the body as `event` and the envelope (headers, method…)
// as `output`; `trigger.*` reads them as one object.
const triggerValue = (sample: TDraftSample | undefined): unknown => {
  if (!sample) return undefined;
  const envelope = isRecord(sample.output) ? sample.output : {};
  return sample.event === null || sample.event === undefined
    ? envelope
    : { ...envelope, body: sample.event };
};

const ORDER = [
  'flows.template_parallel_branch',
  'flows.template_other_branch',
  'flows.template_not_on_every_path',
  'flows.template_inside_loop',
  'flows.template_self_reference',
  'flows.template_downstream',
];

// The validator names the step first ("Deliver runs next to Slack…"); the picker prints it
// under that step's own name, as the design does ("Runs next to Slack…").
const underName = (message: string, name: string): string => {
  let text = message.startsWith(`${name} `) ? message.slice(name.length + 1) : message;
  if (text.startsWith('is ')) text = text.slice(3);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

interface IReaderInput {
  graph: TFlowGraph;
  reader: string;
  samples?: readonly TDraftSample[];
  chosen?: TSampleTrigger | null;
  known?: TKnownFields | null;
  timeZone?: string;
}

type TKnownField = NonNullable<TKnownFields['trigger']>['fields'][number];

const knownField = (field: TKnownField, prefix: string): IDataField => {
  const value = field.type === 'list' ? [] : field.sample;
  return {
    path: field.path,
    label: field.path.slice(prefix.length + 1),
    type: field.type,
    value,
    preview:
      field.items !== null
        ? countOf(field.items, 'item')
        : field.sample === null
          ? field.type === 'null'
            ? 'null'
            : ''
          : sampleText(field.sample),
  };
};

interface IFound {
  fields: IDataField[];
  origin?: string;
  missing?: string;
}

// The last test run first, then the sample "Test with" names; paths only other recent
// requests had are added after, so a field the newest one lacked can still be picked.
const triggerFields = (
  sample: TDraftSample | undefined,
  chosen: TSampleTrigger | null | undefined,
  known: TKnownFields | null | undefined,
  timeZone: string | undefined,
): IFound => {
  let fields: IDataField[] = [];
  let origin: string | undefined;
  if (sample) {
    fields = fieldsOf(triggerValue(sample), 'trigger');
    origin = 'from the last test run';
  } else if (chosen) {
    fields = fieldsOf(chosen.trigger, 'trigger');
    origin = chosen.at
      ? `from ${chosen.label} · ${pointTime(chosen.at, Date.now(), timeZone)}`
      : `from ${chosen.label}`;
  }
  const schema = known?.trigger;
  if (schema && schema.fields.length > 0) {
    const have = new Set(fields.map((field) => field.path));
    const more = schema.fields
      .filter((field) => !have.has(field.path))
      .map((field) => knownField(field, 'trigger'));
    const last = `last ${countOf(schema.scanned, 'request')}`;
    if (more.length > 0) {
      origin = origin ? `${origin} + ${last}` : `seen in the ${last}`;
      fields = [...fields, ...more].slice(0, MAX_FIELDS);
    }
  }
  if (fields.length > 0) return { fields, origin };
  return {
    fields,
    missing:
      schema && schema.scanned === 0
        ? 'Its source has no requests yet, so its fields are not known.'
        : NOT_TESTED,
  };
};

const stepFields = (
  key: string,
  sample: TDraftSample | undefined,
  known: TKnownFields | null | undefined,
  timeZone: string | undefined,
): IFound => {
  if (sample) {
    return { fields: fieldsOf(sample.output, `nodes.${key}`), origin: 'from the last test run' };
  }
  const live = known?.steps.find((step) => step.nodeKey === key);
  if (live) {
    return {
      fields: fieldsOf(live.output, `nodes.${key}`),
      origin: `from a live run · ${pointTime(live.at, Date.now(), timeZone)}`,
    };
  }
  if (!known) return { fields: [], missing: NOT_TESTED };
  return {
    fields: [],
    missing: known.liveNodeKeys.includes(key)
      ? 'Not in the last test run, and no recent live run kept what it produced.'
      : 'Not published yet and not in the last test run, so its fields are not known yet.',
  };
};

const countOf = (n: number, noun: string): string =>
  n === 1 ? `1 ${noun}` : `${n.toLocaleString('en-US')} ${noun}s`;

const EMPTY: IReaderData = {
  sources: [],
  unreadable: [],
  context: null,
  names: new Map(),
  explain: () => null,
};

export const readerData = ({
  graph,
  reader,
  samples = [],
  chosen,
  known,
  timeZone,
}: IReaderInput): IReaderData => {
  let index: IGraphIndex;
  let dominance: IDominance;
  try {
    index = buildGraphIndex(graph);
    dominance = buildDominance(index);
  } catch {
    return EMPTY;
  }
  const names = new Map([...index.nodes].map(([key, node]) => [key, node.name]));
  const namer = { name: (key: string) => names.get(key) ?? key };
  const byKey = new Map(samples.map((sample) => [sample.nodeKey, sample]));
  const visible = new Set(dominance.visible(reader));
  if (index.trigger) visible.add(index.trigger);

  const explain = (target: string): string | null => {
    if (target !== reader && visible.has(target)) return null;
    return explainReference(index, dominance, reader, target, namer).message;
  };

  // Steps a waiting Merge joined sit right under that Merge, nearest first like the chain.
  const chain = dominance.chain(reader);
  const joined = [...visible]
    .filter((key) => !chain.includes(key) && key !== index.trigger)
    .sort((a, b) => dominance.visible(b).size - dominance.visible(a).size);
  const order = [
    ...chain.slice(0, 1),
    ...joined,
    ...chain.slice(1),
    ...(index.trigger && !chain.includes(index.trigger) ? [index.trigger] : []),
  ];
  const sources: IDataSource[] = order.flatMap((key) => {
    const node = index.nodes.get(key);
    if (!node || key === reader) return [];
    const sample = byKey.get(key);
    const found =
      key === index.trigger
        ? triggerFields(sample, chosen, known, timeZone)
        : stepFields(key, sample, known, timeZone);
    return [
      {
        key,
        name: node.name,
        type: node.type,
        hasSample: sample !== undefined,
        fields: found.fields,
        origin: found.origin,
        missing: found.missing,
      },
    ];
  });

  const unreadable = [...index.nodes.values()]
    .filter((node) => node.key !== reader && !visible.has(node.key))
    .map((node) => ({ node, problem: explainReference(index, dominance, reader, node.key, namer) }))
    .sort((a, b) => rank(a.problem.code) - rank(b.problem.code))
    .map(({ node, problem }) => ({
      key: node.key,
      name: node.name,
      type: node.type,
      reason: underName(problem.message, node.name),
    }));

  const predecessor = index.in.get(reader)?.[0]?.from;
  const nodes: Record<string, unknown> = {};
  for (const key of visible) {
    const sample = byKey.get(key);
    if (sample && key !== index.trigger) nodes[key] = sample.output;
  }
  const trigger = index.trigger ? byKey.get(index.trigger) : undefined;
  const event = predecessor ? byKey.get(predecessor)?.event : trigger?.event;
  const context: IFlowNodeContext | null =
    samples.length === 0
      ? null
      : {
          trigger: triggerValue(trigger) ?? null,
          event: event ?? null,
          item: null,
          loop: null,
          nodes,
          flow: { id: '', versionNo: 0, name: '' },
          run: { id: '', at: '' },
        };

  // Right after the trigger the event is the request body, so its fields are the body's.
  const start = sources.find((source) => source.key === index.trigger);
  const bodyEvent: IDataSource | undefined =
    context === null && predecessor !== undefined && predecessor === index.trigger && start
      ? {
          key: 'event',
          name: 'Event',
          type: 'event',
          hasSample: false,
          origin: start.origin,
          missing: start.missing,
          fields: start.fields.flatMap((field) => {
            if (field.path !== 'trigger.body' && !field.path.startsWith('trigger.body.')) return [];
            const path = `event${field.path.slice('trigger.body'.length)}`;
            return [{ ...field, path, label: path === 'event' ? 'event' : path.slice(6) }];
          }),
        }
      : undefined;

  return { sources, unreadable, context, names, explain, event: bodyEvent };
};

const rank = (code: string): number => {
  const at = ORDER.indexOf(code);
  return at === -1 ? ORDER.length : at;
};

export const eventSource = (data: IReaderData, name: string): IDataSource | null => {
  const event = data.context?.event;
  if (event === null || event === undefined) {
    return data.event && data.event.fields.length > 0 ? { ...data.event, name } : null;
  }
  return {
    key: 'event',
    name,
    type: 'event',
    hasSample: true,
    fields: fieldsOf(event, 'event'),
    origin: 'from the last test run',
  };
};

export const matchesQuery = (source: IDataSource, field: IDataField, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    field.path.toLowerCase().includes(needle) ||
    field.label.toLowerCase().includes(needle) ||
    source.name.toLowerCase().includes(needle)
  );
};

/** A field's path as a DOM id, for `aria-activedescendant` — paths hold `.`, `[`, `]`. */
export const optionId = (listboxId: string, path: string): string =>
  `${listboxId}-${path.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

export const pickableFields = (sources: readonly IDataSource[], query: string): IDataField[] =>
  sources.flatMap((source) => source.fields.filter((field) => matchesQuery(source, field, query)));

export const findField = (
  sources: readonly IDataSource[],
  path: string,
): IDataField | undefined => {
  for (const source of sources) {
    const field = source.fields.find((candidate) => candidate.path === path);
    if (field) return field;
  }
  return undefined;
};

export interface IRefLabel {
  text: string;
  broken: boolean;
  title?: string;
}

// `nodes.n_code.amount` reads as `Code.amount`: the step's name, never its key (§5.3).
export const refLabel = (ref: IContextPath, data: IReaderData): IRefLabel => {
  if (ref.root !== 'nodes' || !ref.nodeKey) return { text: ref.path, broken: false };
  const rest = ref.path.split('.').slice(2).join('.');
  const name = data.names.get(ref.nodeKey);
  const problem = data.explain(ref.nodeKey);
  const head = name ?? ref.nodeKey;
  return {
    text: rest ? `${head}.${rest}` : head,
    broken: problem !== null,
    title: problem ?? ref.path,
  };
};
